import {SaveAlt} from "@mui/icons-material";
import {LoadingButton} from "@mui/lab";
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    Stack,
} from "@mui/material";
import {Field, Form, Formik, FormikHelpers} from "formik";
import * as yup from "yup";
import type {Auftrag, TestSzenario} from "../../api";
import Fieldset from "../../components/Fieldset";
import Guard, {useBerechtigt} from "../../components/Guard";
import TestSzenarioVersionSelect from "../../components/TestSzenarioVersionSelect";
import {useSetzeAuftrag} from "./data";
import {useCallback} from "react";
import {useNavigate} from "react-router";

const schema = yup.object({
    testSzenario: yup.object({ id: yup.string().required() }).required(),
});

type FormValues = yup.InferType<typeof schema>;

export default function AuftragSzenarioForm({
                                                auftrag,
                                                testSzenario,
                                                onClose,
                                            }: {
    auftrag: Auftrag;
    testSzenario: TestSzenario;
    onClose: () => void;
}) {
    const berechtigung = auftrag
        ? "auftrag:bearbeiten"
        : "auftrag:erstellen";
    const enabled = useBerechtigt({berechtigung});

    const initialValues: FormValues = {
        testSzenario: testSzenario ?? "",
    };

    const { mutate: setzeAuftrag } = useSetzeAuftrag();

    const navigate = useNavigate();

    const onSubmit = useCallback(
        async (
            { testSzenario }: FormValues,
            { setStatus, setSubmitting }: FormikHelpers<FormValues>
        ) => {
            setStatus(undefined);

            try {
                setzeAuftrag({
                    id: auftrag.id,
                    daten: { ...auftrag, testSzenarioVersionId: testSzenario.id },
                })
                navigate(`/auftraege/${auftrag.id}`);
            } catch (e: unknown) {
                setStatus(e);
                setSubmitting(false);
            }
        },
        [setzeAuftrag, navigate]
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={onSubmit}
        >
            {({isSubmitting, isValid, dirty, status, values}) => (
                <>
                    <DialogContent>
                        <Box component={Form} id="auftrag-szenario-form" noValidate sx={{pt: 1}}>
                            <Stack component={Fieldset} spacing={2} disabled={!enabled}>
                                <Field
                                    component={TestSzenarioVersionSelect}
                                    name="testSzenario"
                                    label="Test-Szenario"
                                    servoTypId={auftrag.servoTypId}
                                    required
                                />
                                {status && <Alert severity="error">{status.message}</Alert>}
                            </Stack>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            type="button"
                            variant="text"
                            color="inherit"
                            onClick={onClose}
                        >
                            Abbrechen
                        </Button>
                            <Guard berechtigung={berechtigung}>
                                <LoadingButton
                                    type="submit"
                                    form="auftrag-szenario-form"
                                    variant="contained"
                                    startIcon={<SaveAlt/>}
                                    loading={isSubmitting}
                                    disabled={!isValid || !dirty}
                                >
                                    Speichern
                                </LoadingButton>
                            </Guard>
                    </DialogActions>
                </>
            )}
        </Formik>
    );
}
